import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Alert, Box, Button, Skeleton, Snackbar, Tab, Tabs, Typography } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import RoundedPaper from 'shared/components/RoundedPaper';
import GoogleWatchdogService from 'services/GoogleWatchdogService';
import useActorName from 'shared/hooks/useActorName';
import useKeymap from 'shared/hooks/useKeymap';
import { TEAM } from 'shared/constants/team';
import { ACTION_STEP_STORES, ActionItem, CampaignOption, CardProblemFigure, UserCampaignGroup, WatchdogHealth } from './types';
import { itemCache, listCache } from './cache';
import {
  applyOwner, BRANDS, Brand, DEFAULT_BRAND, hasUnmappedCampaign, inScope,
  isListCard, isSystemAlert, itemStores, localDay, parseTs, parseWatchdogQuery,
  serializeWatchdogQuery, sortWatchdogItems, storesForBrand, TODAY_CAP, OwnerMode, WatchdogQuery,
} from './logic';
import CampaignPicker from './components/CampaignPicker';
import { buttonGroup, plainButton, pressable, srOnly } from './controls';
import { DismissDialog, ResolveDialog, SnoozeDialog } from './components/ActionDialogs';
import CommandPalette, { PaletteAction } from './components/CommandPalette';
import BulkBar, { BulkAction } from './components/BulkBar';
import PerformanceTable, { MineFilter } from './components/PerformanceTable';
import ActionSteps from './components/ActionSteps';
import { cardWindows } from './performance';

dayjs.extend(utc);
dayjs.extend(timezone);

const service = new GoogleWatchdogService();
const RESTORE_KEY = 'sbly-tools:google-watchdog-restore';
const REVALIDATE_MS = 300_000;
const PT = 'America/Los_Angeles';

// From sbly-scheduler's intelligence-watchdog DAG (dags/intelligence.py): "0 6,15 * * *"
// with America/Los_Angeles as the timezone.
export const WATCHDOG_RUN_HOURS_PT = [6, 15] as const;
const DAY = 86_400_000;
const HEALTH_GRACE_MS = 90 * 60 * 1000;

// Amber marks the Urgent queue: its box, the brand tab underline, the place numbers and "New
// today". It is never a severity colour; red and green stay on the moved numbers.
const TODAY = { accent: '#D49838', ink: '#834B14', wash: '#FBF2DE', line: '#E8D1AC' } as const;
const rowColumns = (showStore: boolean) => showStore ? '30px 42px minmax(0, 1fr) 210px 84px 88px' : '30px minmax(0, 1fr) 210px 84px 88px';
// The checkbox column at the start of every row, outside the row's open button (a checkbox can't
// live inside a button). The header, skeleton and closed rows keep the same space so columns line up.
const CHECK_WIDTH = '40px';
// Opaque, so a ticked row reads the same over the amber Urgent wash as over white.
const CHECKED_TINT = '#ECEDFE';
// Room at the end of every row for the button that opens its numbers, kept on rows that have none
// so the columns still line up with the header.
const TOGGLE_WIDTH = '36px';
// Where the Problem column starts (row padding + # column + gap, plus the store column), so an
// opened row's numbers sit under its headline.
const problemIndent = (showStore: boolean) => (showStore ? '140px' : '84px');

type DialogKind = 'resolve' | 'dismiss' | 'snooze' | null;

function plural(count: number, word: string): string {
  return `${count} ${word}${count === 1 ? '' : 's'}`;
}

/** Runs one request per card side by side; one card failing never stops the others. */
async function forEachCard(ids: string[], fn: (id: string) => Promise<void>): Promise<{ done: string[]; failed: string[]; firstError: string }> {
  const results = await Promise.allSettled(ids.map(id => fn(id)));
  const done = ids.filter((_, index) => results[index].status === 'fulfilled');
  const failed = ids.filter((_, index) => results[index].status === 'rejected');
  const firstRejection = results.find((result): result is PromiseRejectedResult => result.status === 'rejected');
  return { done, failed, firstError: firstRejection ? String(firstRejection.reason?.message ?? firstRejection.reason) : '' };
}

/** "Claimed 3 cards", or "Claimed 2 of 3 cards. 1 failed: <reason>". */
function bulkMessage(verb: string, total: number, outcome: { done: string[]; failed: string[]; firstError: string }, suffix = ''): string {
  if (!outcome.failed.length) return `${verb} ${plural(total, 'card')}${suffix}`;
  return `${verb} ${outcome.done.length} of ${plural(total, 'card')}${suffix}. ${outcome.failed.length} failed: ${outcome.firstError}`;
}

function problemHeadline(item: ActionItem): string {
  return item.card?.problem?.headline ?? item.headline ?? 'Watchdog card';
}

function problemSubject(item: ActionItem): string {
  return item.card?.problem?.subject ?? item.campaign_name ?? item.unit_label ?? item.store ?? item.item_id;
}

function searchText(item: ActionItem): string {
  return [problemHeadline(item), problemSubject(item), item.store, item.unit_label].filter(Boolean).join(' ').toLowerCase();
}

function figureColor(tone: 'bad' | 'good' | 'neutral'): string {
  return tone === 'bad' ? '#DC2626' : tone === 'good' ? '#059669' : '#0F172A';
}

function Figure({ figure }: { figure: CardProblemFigure | null }) {
  if (!figure) return <Box aria-label="No comparison available" sx={{ minHeight: 20 }} />;
  if (figure.kind === 'text') {
    return <Typography sx={{ color: '#475569', fontSize: '0.8125rem', lineHeight: 1.35 }}>{figure.text}</Typography>;
  }
  const word = figure.tone === 'bad' ? 'worse' : figure.tone === 'good' ? 'better' : 'changed';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
      <Typography component="span" sx={{ color: '#475569', fontSize: '0.875rem' }}>{figure.before}</Typography>
      <Typography component="span" aria-hidden="true" sx={{ color: '#64748B', fontSize: '0.875rem' }}>→</Typography>
      <Typography component="span" sx={{ color: '#0F172A', fontSize: '0.875rem', fontWeight: 600 }}>{figure.after}</Typography>
      <Typography component="span" aria-label={word} sx={{ color: figureColor(figure.tone), fontSize: '0.625rem', lineHeight: 1 }}>
        {figure.dir === 'up' ? '▲' : '▼'}
      </Typography>
      <Box component="span" sx={srOnly}>{word}</Box>
    </Box>
  );
}

function ageLabel(firstSeen: string, now: Date): { label: string; isNew: boolean } {
  if (localDay(firstSeen) === localDay(now.toISOString())) return { label: 'New today', isNew: true };
  const first = new Date(`${localDay(firstSeen)}T00:00:00`).getTime();
  const today = new Date(`${localDay(now.toISOString())}T00:00:00`).getTime();
  const days = Math.max(1, Math.round((today - first) / DAY));
  return { label: `${days} day${days === 1 ? '' : 's'}`, isNew: false };
}

function ownerLabel(item: ActionItem, actorName: string): { text: string; sr: string } {
  if (!item.claimed_by) return { text: '', sr: 'Unclaimed' };
  if (item.claimed_by === actorName) return { text: 'You', sr: 'You' };
  return { text: item.claimed_by, sr: item.claimed_by };
}

/** The Google Ads campaign a card is about, when it is about one, for the numbers under its row. */
function rowCampaign(item: ActionItem): { store: string; campaignId: string } | null {
  return item.campaign_id && item.store ? { store: item.store, campaignId: item.campaign_id } : null;
}

function WatchdogRow({
  item, index, showStore, selected, actorName, now, closed, today, withNumbers = false, open = false, onToggle, onOpen,
  checked = false, onCheck,
}: {
  item: ActionItem;
  index: number | null;
  showStore: boolean;
  selected: boolean;
  actorName: string;
  now: Date;
  closed?: boolean;
  today?: boolean;
  /** The campaign numbers view (/google-watchdog/triage): each row keeps room for its toggle. */
  withNumbers?: boolean;
  /** Whether the row's numbers are showing. The list owns this so Expand all can set every row. */
  open?: boolean;
  onToggle?: (itemId: string) => void;
  onOpen: (item: ActionItem) => void;
  /** Ticked for a bulk action. */
  checked?: boolean;
  /** Present on open cards only; closed cards can't be acted on. `range` is a shift-click. */
  onCheck?: (itemId: string, range: boolean) => void;
}) {
  const age = closed ? { label: '3 quiet days', isNew: false } : ageLabel(item.first_seen, now);
  const owner = ownerLabel(item, actorName);
  const figure = closed ? item.card?.recovery?.figure ?? null : item.card?.problem?.figure ?? null;
  // Cards about a campaign open onto its numbers; page and store cards have no campaign to show.
  const campaign = closed || !withNumbers ? null : rowCampaign(item);
  const panelId = `watchdog-numbers-${item.item_id}`;
  return (
    <Box
      sx={{
        borderTop: `1px solid ${today ? TODAY.line : '#E2E8F0'}`,
        bgcolor: checked ? CHECKED_TINT : selected ? 'rgba(99,102,241,0.08)' : 'transparent',
        '&:hover': checked || selected ? {} : { bgcolor: 'rgba(100,116,139,0.06)' },
      }}
    >
    <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
    <Box
      component={onCheck ? 'label' : 'div'}
      // Keeps focus on the page, so J, K and Space carry on working after a click.
      onMouseDown={onCheck ? (event: React.MouseEvent) => event.preventDefault() : undefined}
      sx={{ width: CHECK_WIDTH, flexShrink: 0, display: 'flex', alignItems: 'center', pl: 1.75, cursor: onCheck ? 'pointer' : 'default' }}
    >
      {onCheck && (
        <Box
          component="input"
          type="checkbox"
          checked={checked}
          aria-label={`Select: ${problemHeadline(item)}, ${problemSubject(item)}`}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => onCheck(item.item_id, (event.nativeEvent as MouseEvent).shiftKey)}
          sx={{ m: 0, width: 16, height: 16, cursor: 'pointer', accentColor: '#4F46E5', '&:focus-visible': { outline: '2px solid #6366F1', outlineOffset: 2 } }}
        />
      )}
    </Box>
    <Box
      component="button"
      type="button"
      data-item-id={item.item_id}
      onClick={(event: React.MouseEvent) => {
        // Shift- or Cmd-click ticks the row instead of opening it, as in a mail client.
        if (onCheck && (event.shiftKey || event.metaKey || event.ctrlKey)) { event.preventDefault(); onCheck(item.item_id, event.shiftKey); return; }
        onOpen(item);
      }}
      sx={{
        display: 'grid',
        gridTemplateColumns: rowColumns(showStore),
        columnGap: 1.75,
        alignItems: 'center',
        flex: 1,
        minWidth: 0,
        pl: 0,
        pr: 1.75,
        py: 1.25,
        textAlign: 'left',
        border: 0,
        bgcolor: 'transparent',
        color: 'inherit',
        cursor: 'pointer',
        '&:focus-visible': { outline: '2px solid #6366F1', outlineOffset: -2 },
      }}
    >
      <Typography sx={{ color: today ? TODAY.ink : '#64748B', textAlign: 'right', fontSize: '0.875rem', fontWeight: today ? 700 : 600, lineHeight: 1.45, fontVariantNumeric: 'tabular-nums' }}>
        {index ?? ''}
      </Typography>
      {showStore && <Typography sx={{ width: 36, color: '#475569', border: '1px solid #CBD5E1', borderRadius: '4px', py: 0.5, textAlign: 'center', fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.04em', lineHeight: 1 }}>{item.store ?? itemStores(item)?.[0] ?? ''}</Typography>}
      <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.125 }}>
        <Typography sx={{ color: closed ? '#475569' : '#0F172A', fontSize: '0.90625rem', fontWeight: closed ? 500 : 700, lineHeight: 1.45, overflowWrap: 'anywhere' }}>{closed ? 'Recovered, closed automatically' : problemHeadline(item)}</Typography>
        <Typography sx={{ color: '#475569', fontSize: '0.8125rem', lineHeight: 1.45, overflowWrap: 'anywhere' }}>{problemSubject(item)}</Typography>
      </Box>
      <Box sx={{ minWidth: 0 }}><Figure figure={figure} /></Box>
      <Typography sx={{ color: age.isNew ? TODAY.ink : '#475569', fontSize: '0.8125rem', fontWeight: age.isNew ? 700 : 400, textAlign: 'right', whiteSpace: 'nowrap' }}>{age.label}</Typography>
      <Typography sx={{ color: owner.text === 'You' ? '#0F172A' : '#475569', fontSize: '0.8125rem', fontWeight: owner.text === 'You' ? 700 : 400, textAlign: 'right', whiteSpace: 'nowrap' }}>
        {owner.text}<Box component="span" sx={srOnly}>{owner.sr}</Box>
      </Typography>
    </Box>
    {withNumbers && (
    <Box sx={{ width: TOGGLE_WIDTH, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {campaign && onToggle && (
        <Box
          component="button"
          type="button"
          aria-expanded={open}
          aria-controls={panelId}
          aria-label={open ? 'Hide numbers' : 'Show numbers'}
          title={open ? 'Hide numbers' : 'Show campaign numbers'}
          onClick={() => onToggle(item.item_id)}
          sx={{ ...plainButton, display: 'flex', p: '2px', border: 0, borderRadius: '4px', bgcolor: 'transparent', color: '#64748B', '&:hover': { bgcolor: 'rgba(100,116,139,0.12)', color: '#0F172A' } }}
        >
          <KeyboardArrowDownIcon aria-hidden="true" sx={{ fontSize: 20, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }} />
        </Box>
      )}
    </Box>
    )}
    </Box>
    {campaign && open && (
      <Box id={panelId} sx={{ pl: problemIndent(showStore), pr: TOGGLE_WIDTH, pb: 1.5 }}>
        <PerformanceTable stores={[campaign.store]} showStore={false} now={now} campaignId={campaign.campaignId} windows={cardWindows(item.card?.costOfDelay?.dropStart, item.card?.series?.roas.map(point => point.date) ?? [])} />
      </Box>
    )}
    </Box>
  );
}

function ColumnHeader({ showStore, withNumbers = false }: { showStore: boolean; withNumbers?: boolean }) {
  return (
    <Box aria-hidden="true" sx={{ display: 'grid', gridTemplateColumns: rowColumns(showStore), columnGap: 1.75, pl: CHECK_WIDTH, pr: 1.75, mr: withNumbers ? TOGGLE_WIDTH : 0, pb: 0.75, color: '#64748B', fontSize: '0.65625rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
      <span style={{ textAlign: 'right' }}>#</span>
      {showStore && <span>Store</span>}
      <span>Problem</span>
      <span>Before → after</span>
      <span style={{ textAlign: 'right' }}>Age</span>
      <span style={{ textAlign: 'right' }}>Owner</span>
    </Box>
  );
}

function SkeletonRows({ showStore, withNumbers = false }: { showStore: boolean; withNumbers?: boolean }) {
  const bar = (height: number) => <Skeleton variant="rounded" height={height} sx={{ bgcolor: '#EEF2F6', borderRadius: '4px' }} />;
  return <>{Array.from({ length: 7 }, (_, index) => (
    <Box key={index} sx={{ display: 'grid', gridTemplateColumns: rowColumns(showStore), columnGap: 1.75, alignItems: 'center', pl: CHECK_WIDTH, pr: 1.75, ...(withNumbers ? { pr: `calc(14px + ${TOGGLE_WIDTH})` } : {}), py: 1.75, borderTop: `1px solid ${TODAY.line}` }}>
      {bar(11)}
      {showStore && bar(11)}
      {bar(30)}
      {bar(11)}
      {bar(11)}
      {bar(11)}
    </Box>
  ))}</>;
}

function SectionHead({ id, title, note }: { id?: string; title: string; note?: string }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: '4px 12px', px: 1.75, mb: 1 }}>
      <Typography component="h2" id={id} sx={{ fontSize: '1.0625rem', fontWeight: 700, lineHeight: 1.2 }}>{title}</Typography>
      {note && <Typography sx={{ color: '#475569', fontSize: '0.8125rem' }}>{note}</Typography>}
    </Box>
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  return <Box component="kbd" sx={{ font: 'inherit', fontSize: '0.6875rem', fontWeight: 600, lineHeight: 1, border: '1px solid #CBD5E1', borderBottomWidth: 2, borderRadius: '4px', px: '5px', py: '2px', bgcolor: '#FFFFFF', color: '#475569' }}>{children}</Box>;
}


function formatTime(value: string | null | undefined): string {
  if (!value) return 'not available';
  return parseTs(value).toLocaleTimeString('en-US', { timeZone: PT, hour: 'numeric', minute: '2-digit' });
}

function nextSlot(now: Date): string {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: PT, hour: 'numeric', minute: 'numeric', hour12: false }).formatToParts(now);
  const hour = Number(parts.find(part => part.type === 'hour')?.value ?? 0);
  const minute = Number(parts.find(part => part.type === 'minute')?.value ?? 0);
  const next = WATCHDOG_RUN_HOURS_PT.find(slot => slot * 60 > hour * 60 + minute) ?? WATCHDOG_RUN_HOURS_PT[0];
  return new Date(2000, 0, 1, next).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

// The newest scheduled run whose grace window has closed. The slot is resolved as a PT wall-clock
// time on its own date, so the night the clocks change still lands on the real run.
function expectedRun(now: Date): number {
  const cutoff = dayjs(now.getTime() - HEALTH_GRACE_MS).tz(PT);
  const due = WATCHDOG_RUN_HOURS_PT.filter(slot => slot <= cutoff.hour());
  const date = due.length ? cutoff.format('YYYY-MM-DD') : dayjs(cutoff.format('YYYY-MM-DD')).subtract(1, 'day').format('YYYY-MM-DD');
  const slot = due.length ? due[due.length - 1] : WATCHDOG_RUN_HOURS_PT[WATCHDOG_RUN_HOURS_PT.length - 1];
  return dayjs.tz(`${date} ${String(slot).padStart(2, '0')}:00`, PT).valueOf();
}

function healthMessages(health: WatchdogHealth | null | undefined, now: Date): string[] {
  if (health == null) return [health === undefined ? 'Watchdog pipeline is not delivering, health status could not be loaded.' : 'Watchdog pipeline is not delivering, no successful watchdog run is on record.'];
  if (health.error != null) return [`Watchdog pipeline is not delivering, latest run failed: ${health.error}.`];
  const lastSuccess = health.last_success_at ? parseTs(health.last_success_at).getTime() : null;
  if (health.hours_since_last_success == null || (lastSuccess != null && lastSuccess < expectedRun(now))) {
    return [`Watchdog pipeline is not delivering, the last successful run was ${lastSuccess == null ? 'not recorded' : `${Math.round((now.getTime() - lastSuccess) / 3_600_000)} hours ago`}.`];
  }
  const messages: string[] = [];
  if (health.skipped_stores?.length) messages.push(`Latest run skipped ${health.skipped_stores.map(store => `${store.store} (${store.reason ?? 'no data'})`).join(', ')}, so items for these stores were not scanned today.`);
  const raw = health.failed_evaluators;
  const failed = Array.isArray(raw) ? raw : typeof raw === 'string' ? (() => { try { const parsed = JSON.parse(raw); return Array.isArray(parsed) ? parsed : []; } catch { return []; } })() : [];
  if (failed.length) {
    const names = failed.map(name => {
      const words = name.replace(/Evaluator$/, '').replace(/([a-z0-9])([A-Z])/g, '$1 $2');
      return words.charAt(0).toUpperCase() + words.slice(1);
    });
    messages.push(`${failed.length} ${failed.length === 1 ? 'check' : 'checks'} failed in the latest run: ${names.join(', ')}. Their findings are missing today.`);
  }
  if (health.still_detected === null) messages.push('Still-detected list unavailable, so dismissed cards that are still firing are not shown here.');
  else if (health.still_detected?.length) messages.push(`${health.still_detected.length} dismissed ${health.still_detected.length === 1 ? 'problem is' : 'problems are'} still being detected. Each comes back by its check-up date, or sooner if it gets worse.`);
  return messages;
}

function joinNames(names: string[]): string {
  if (names.length < 2) return names[0] ?? '';
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
}

function unmappedMessages(items: ActionItem[]): string[] {
  const byStore = new Map<string, Set<string>>();
  for (const item of items) {
    if (!hasUnmappedCampaign(item)) continue;
    const names = itemStores(item) ?? [item.store ?? ''];
    for (const store of names) {
      const set = byStore.get(store) ?? new Set<string>();
      set.add(item.campaign_name ?? item.unit_label ?? item.store ?? item.item_id);
      byStore.set(store, set);
    }
  }
  return [...byStore.entries()].filter(([store]) => store).map(([, names]) => {
    const values = [...names];
    if (values.length === 1) return `${values[0]} has no break-even on file, so it can only be compared to its target.`;
    return `${values.length} campaigns have no break-even on file, so they can only be compared to their target: ${joinNames(values)}.`;
  });
}

// Counts wait for the list, so a loading page never claims a brand or store has nothing open.
function BrandTabs({ query, items, loading, onChange }: { query: WatchdogQuery; items: ActionItem[]; loading: boolean; onChange: (next: Partial<WatchdogQuery>) => void }) {
  const count = (brand: Brand) => items.filter(item => isListCard(item) && inScope(item, brand)).length;
  return (
    <Tabs
      value={query.brand}
      onChange={(_, value: Brand) => onChange({ brand: value, store: undefined })}
      TabIndicatorProps={{ sx: { height: 3, bgcolor: TODAY.accent, borderRadius: '2px 2px 0 0' } }}
      sx={{ minHeight: 0, borderBottom: '1px solid #E2E8F0', '& .MuiTabs-flexContainer': { gap: '22px' }, '& .MuiTab-root': { minHeight: 0, minWidth: 0, px: 0, pt: '10px', pb: '11px', fontSize: '0.9375rem', fontWeight: 700, color: '#475569' }, '& .MuiTab-root.Mui-selected': { color: '#0F172A', fontWeight: 700 } }}
    >
      {BRANDS.map(candidate => (
        <Tab key={candidate.key} value={candidate.key} label={<span>{candidate.label}{!loading && <>{' '}<Box component="span" sx={{ ml: '2px', color: '#64748B', fontSize: '0.75rem', fontWeight: 400 }}>{count(candidate.key)}</Box></>}</span>} />
      ))}
    </Tabs>
  );
}

function StoreChips({ query, items, loading, onChange }: { query: WatchdogQuery; items: ActionItem[]; loading: boolean; onChange: (next: Partial<WatchdogQuery>) => void }) {
  const brand = BRANDS.find(candidate => candidate.key === query.brand) ?? BRANDS[0];
  if (brand.stores.length < 2) return null;
  const count = (store?: string) => items.filter(item => isListCard(item) && inScope(item, query.brand, store)).length;
  return (
    <Box role="group" aria-label="Store" sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
      {[undefined, ...brand.stores].map(store => {
        const pressed = query.store === store;
        return (
          <Box
            key={store ?? 'all'}
            component="button"
            type="button"
            aria-pressed={pressed}
            onClick={() => onChange({ store })}
            sx={{ ...plainButton, fontSize: '0.8125rem', fontWeight: 500, border: `1px solid ${pressed ? '#0F172A' : '#CBD5E1'}`, borderRadius: '999px', px: '11px', py: '4px', bgcolor: pressed ? '#0F172A' : '#FFFFFF', color: pressed ? '#FFFFFF' : '#475569', '&:hover': { borderColor: pressed ? '#0F172A' : '#94A3B8' } }}
          >
            {store ?? 'All'}
            {!loading && <>{' '}<Box component="span" sx={{ ml: '2px', fontSize: '0.75rem', fontWeight: 400, color: pressed ? '#FFFFFF' : '#64748B', opacity: pressed ? 0.75 : 1 }}>{count(store)}</Box></>}
          </Box>
        );
      })}
    </Box>
  );
}

/**
 * The Watchdog list. With campaignNumbers (the /google-watchdog/triage route) it also shows the
 * Campaign performance table under the system alerts, and each card about a campaign opens onto
 * that campaign's numbers.
 */
export default function GoogleWatchdog({ campaignNumbers = false }: { campaignNumbers?: boolean }) {
  const history = useHistory();
  const location = useLocation();
  const [actorName, setActorName] = useActorName();
  const query = useMemo(() => parseWatchdogQuery(location.search), [location.search]);
  const setQuery = useCallback((patch: Partial<WatchdogQuery>) => {
    const next = { ...query, ...patch };
    history.replace({ pathname: location.pathname, search: serializeWatchdogQuery(next) });
  }, [history, location.pathname, query]);
  const owner: OwnerMode = query.owner ?? 'anyone';
  const cacheSeed = useRef(listCache.items !== null);
  const [items, setItems] = useState<ActionItem[]>(() => cacheSeed.current ? listCache.items! : []);
  const [recovered, setRecovered] = useState<ActionItem[]>(listCache.recovered);
  const [recoveredError, setRecoveredError] = useState('');
  const [health, setHealth] = useState<WatchdogHealth | null | undefined>(() => cacheSeed.current ? listCache.health : undefined);
  const [loading, setLoading] = useState(!cacheSeed.current);
  const [loadError, setLoadError] = useState('');
  // A ref, because the persist effect below overwrites listCache.view on mount.
  const savedView = useRef(listCache.view);
  const [q, setQ] = useState(() => savedView.current?.q ?? '');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [dialog, setDialog] = useState<DialogKind>(null);
  // An action step's Approve or Reject dialog is open.
  const [stepDialogOpen, setStepDialogOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [toast, setToast] = useState('');
  const [undo, setUndo] = useState<{ msg: string; itemIds: string[] } | null>(null);
  // Cards ticked for a bulk action, the last one ticked (the start of a shift-click range), the
  // bulk request in flight, and the cards a bulk dialog was opened for.
  const [checked, setChecked] = useState<Set<string>>(() => new Set());
  const anchorId = useRef<string | null>(null);
  const [bulkBusy, setBulkBusy] = useState<BulkAction | null>(null);
  const [bulkDialog, setBulkDialog] = useState<{ kind: 'dismiss' | 'snooze'; ids: string[] } | null>(null);
  const [myGroup, setMyGroup] = useState<UserCampaignGroup | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  // What the picker's search opens with: empty from the pencil, the brand or store from "Add".
  const [pickerFilter, setPickerFilter] = useState('');
  const openPicker = (filter: string) => { setPickerFilter(filter); setPickerOpen(true); };
  // Each campaign's store, from the picker's campaign list (every campaign in any saved list is
  // in it), to count the viewer's campaigns in the brand and store in view.
  const [campaignStores, setCampaignStores] = useState<Map<string, string | null> | null>(null);
  const campaignsRequest = useRef<Promise<CampaignOption[]> | null>(null);
  const [myActive, setMyActive] = useState(() => savedView.current?.myActive ?? false);
  // Closed cards start folded away. Kept in the view cache so opening a closed card and coming
  // back doesn't fold the section shut under the restored scroll position.
  const [closedOpen, setClosedOpen] = useState(() => savedView.current?.closedOpen ?? false);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [now, setNow] = useState(() => new Date());
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const toggleRow = useCallback((itemId: string) => setExpanded(current => {
    const next = new Set(current);
    if (next.has(itemId)) next.delete(itemId); else next.add(itemId);
    return next;
  }), []);

  useEffect(() => { const timer = setInterval(() => setNow(new Date()), 60_000); return () => clearInterval(timer); }, []);

  const load = useCallback(async (background = false) => {
    if (!background) setLoading(true);
    setLoadError('');
    // Fetched on its own, so a failing closed-cards request can't take the open list down with it.
    service.listRecovered().then(closed => {
      setRecovered(closed); setRecoveredError('');
      listCache.recovered = closed;
    }).catch((error: any) => setRecoveredError(error.message));
    try {
      const [list, h] = await Promise.all([service.listItems({}), service.getHealth()]);
      setItems(list); setHealth(h);
      listCache.items = list; listCache.health = h;
      list.forEach(item => itemCache.set(item.item_id, item));
    } catch (error: any) {
      setLoadError(error.message);
    } finally {
      if (!background) setLoading(false);
    }
  }, []);

  useEffect(() => { load(cacheSeed.current); cacheSeed.current = false; }, [load]);
  useEffect(() => {
    const revalidate = () => { if (document.visibilityState === 'visible') load(true); };
    const timer = setInterval(revalidate, REVALIDATE_MS);
    document.addEventListener('visibilitychange', revalidate);
    return () => { clearInterval(timer); document.removeEventListener('visibilitychange', revalidate); };
  }, [load]);
  useEffect(() => { setSelectedId(null); }, [query.brand, query.store]);
  // Declared after the reset above: on a cache-seeded return both run in the same commit, and
  // the later setSelectedId wins, so this order keeps the restored selection.
  useEffect(() => {
    if (loading) return;
    const raw = sessionStorage.getItem(RESTORE_KEY);
    if (!raw) return;
    sessionStorage.removeItem(RESTORE_KEY);
    try {
      const restored = JSON.parse(raw) as { id: string; scrollY: number };
      setSelectedId(restored.id);
      requestAnimationFrame(() => window.scrollTo(0, restored.scrollY));
    } catch { /* stale restore state, ignore */ }
  }, [loading]);
  useEffect(() => {
    let cancelled = false;
    service.listGroups().then(groups => {
      if (cancelled) return;
      const mine = groups.find(group => group.owner === actorName) ?? null;
      // Coming back from a card keeps the user's choice over the default.
      setMyGroup(mine); setMyActive(savedView.current ? savedView.current.myActive : !!mine && mine.campaign_ids.length > 0);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [actorName]);
  useEffect(() => { listCache.view = { q, myActive, closedOpen }; }, [q, myActive, closedOpen]);
  // One request for the campaign list, shared by the picker and the counts; a failed one is
  // forgotten so the picker's Try again asks afresh.
  const loadPickerCampaigns = useCallback(() => {
    if (!campaignsRequest.current) {
      const request = service.listPickerCampaigns();
      campaignsRequest.current = request;
      request.catch(() => { if (campaignsRequest.current === request) campaignsRequest.current = null; });
    }
    return campaignsRequest.current;
  }, []);
  const wantsStores = myActive && !!myGroup?.campaign_ids.length;
  useEffect(() => {
    if (!wantsStores || campaignStores) return;
    let cancelled = false;
    // On failure the counts stay unknown and the messages leave the number out; the picker shows the error.
    loadPickerCampaigns().then(list => { if (!cancelled) setCampaignStores(new Map(list.map(campaign => [campaign.campaignId, campaign.store]))); }, () => {});
    return () => { cancelled = true; };
  }, [wantsStores, campaignStores, loadPickerCampaigns]);

  const scoped = useMemo(() => items.filter(item => inScope(item, query.brand, query.store)), [items, query.brand, query.store]);
  const searched = useMemo(() => scoped.filter(item => !q.trim() || searchText(item).includes(q.trim().toLowerCase())), [scoped, q]);
  const owned = useMemo(() => applyOwner(searched, owner, actorName), [searched, owner, actorName]);
  const effective = useMemo(() => myActive && myGroup ? owned.filter(item => item.campaign_id != null && myGroup.campaign_ids.includes(item.campaign_id)) : owned, [owned, myActive, myGroup]);
  // System alerts and closed rows follow brand and store only: a store-level alert has no
  // campaign, so the work filters would hide it from everyone using My campaigns.
  const systemItems = useMemo(() => scoped.filter(isSystemAlert), [scoped]);
  const unmapped = useMemo(() => scoped.filter(hasUnmappedCampaign), [scoped]);
  const cards = useMemo(() => sortWatchdogItems(effective.filter(isListCard)), [effective]);
  const recoveredView = useMemo(() => recovered.filter(item => inScope(item, query.brand, query.store)), [recovered, query.brand, query.store]);
  const today = cards.slice(0, TODAY_CAP);
  const rest = cards.slice(TODAY_CAP);
  const flat = cards;
  // Rows with numbers to open: cards about a campaign.
  const expandable = useMemo(() => (campaignNumbers ? cards.filter(item => rowCampaign(item) != null).map(item => item.item_id) : []), [campaignNumbers, cards]);
  const allOpen = expandable.length > 0 && expandable.every(id => expanded.has(id));
  const selected = flat.find(item => item.item_id === selectedId) ?? null;
  // Ticks only ever cover cards on screen: a filter, brand switch or refresh that hides a card
  // drops its tick, so a bulk action never reaches a card the viewer can't see.
  useEffect(() => {
    setChecked(current => {
      if (!current.size) return current;
      const visible = new Set(cards.map(item => item.item_id));
      const next = new Set([...current].filter(id => visible.has(id)));
      return next.size === current.size ? current : next;
    });
  }, [cards]);
  const checkedItems = useMemo(() => cards.filter(item => checked.has(item.item_id)), [cards, checked]);
  const claimable = checkedItems.filter(item => item.status === 'open');
  const releasable = checkedItems.filter(item => item.status === 'claimed' && item.claimed_by === actorName);
  const selecting = checkedItems.length > 0;
  const toggleCheck = (itemId: string, range: boolean) => {
    const anchorIndex = range && anchorId.current ? cards.findIndex(item => item.item_id === anchorId.current) : -1;
    const index = cards.findIndex(item => item.item_id === itemId);
    setChecked(current => {
      const next = new Set(current);
      if (anchorIndex >= 0 && index >= 0) {
        // A shift-click gives every card in the range the state the clicked card is about to take.
        const tick = !current.has(itemId);
        for (const item of cards.slice(Math.min(anchorIndex, index), Math.max(anchorIndex, index) + 1)) {
          if (tick) next.add(item.item_id); else next.delete(item.item_id);
        }
      } else if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
    anchorId.current = itemId;
  };
  const clearChecked = () => { setChecked(new Set()); anchorId.current = null; };
  const storesInView = storesForBrand(query.brand, query.store);
  const showStore = storesInView.length > 1;
  const brandLabel = BRANDS.find(brand => brand.key === query.brand)?.label ?? DEFAULT_BRAND;
  const scopeLabel = `${brandLabel}${query.store ? ` ${query.store}` : ''}`;
  const myInView = useMemo(() => {
    if (!myGroup || !campaignStores) return null;
    const stores = storesForBrand(query.brand, query.store);
    return myGroup.campaign_ids.filter(id => { const store = campaignStores.get(id); return store != null && stores.includes(store); }).length;
  }, [myGroup, campaignStores, query.brand, query.store]);
  const mineFilter = useMemo<MineFilter | null>(
    () => (myActive && myGroup ? { ids: new Set(myGroup.campaign_ids), inView: myInView, scope: scopeLabel } : null),
    [myActive, myGroup, myInView, scopeLabel],
  );
  // My campaigns is on and none of them is in this brand or store: one message stands in for
  // both the empty table and the empty list.
  const noneInView = mineFilter?.inView === 0;
  const healthLines = useMemo(() => healthMessages(health, now), [health, now]);
  const systemLines = useMemo(() => [
    ...systemItems.map(problemHeadline), ...unmappedMessages(unmapped), ...healthLines,
    ...(recoveredError ? [`Closed cards could not be loaded: ${recoveredError}.`] : []),
  ], [systemItems, unmapped, healthLines, recoveredError]);

  const move = (delta: number) => {
    if (!flat.length) return;
    const index = flat.findIndex(item => item.item_id === selectedId);
    const next = flat[Math.min(flat.length - 1, Math.max(0, index + delta))] ?? flat[0];
    setSelectedId(next.item_id);
    listRef.current?.querySelector(`[data-item-id="${CSS.escape(next.item_id)}"]`)?.scrollIntoView({ block: 'nearest' });
  };
  // The triage route opens its own version of the card, which carries the agent's action steps.
  const cardBase = campaignNumbers ? '/google-watchdog/triage/items/' : '/google-watchdog/items/';
  const openDetail = (item: ActionItem) => {
    sessionStorage.setItem(RESTORE_KEY, JSON.stringify({ id: item.item_id, scrollY: window.scrollY }));
    history.push({ pathname: `${cardBase}${encodeURIComponent(item.item_id)}`, state: { from: `${location.pathname}${location.search}` } });
  };
  const act = async (fn: () => Promise<void>, success: string) => {
    try { await fn(); setToast(success); } catch (error: any) { setToast(error.message); }
    await load();
  };
  const claimSelected = () => {
    if (!selected) return;
    act(() => service.claim(selected.item_id, selected.status === 'claimed'), selected.status === 'claimed' ? 'Unclaimed' : 'Claimed');
  };
  const bulkClaim = async (unclaim: boolean) => {
    const targets = unclaim ? releasable : claimable;
    if (!targets.length || bulkBusy) return;
    setBulkBusy(unclaim ? 'unclaim' : 'claim');
    try {
      const outcome = await forEachCard(targets.map(item => item.item_id), id => service.claim(id, unclaim));
      setToast(bulkMessage(unclaim ? 'Unclaimed' : 'Claimed', targets.length, outcome));
      // Stays busy until the list reloads, so a second press can't re-claim the same cards.
      await load(true);
    } finally {
      setBulkBusy(null);
    }
  };
  // Dismiss and snooze take the cards off the list, so they offer Undo the way a single card does.
  // The dialog stays open with the error only when every card failed.
  const bulkHold = async (verb: 'Dismissed' | 'Snoozed', ids: string[], fn: (id: string) => Promise<void>, suffix = '') => {
    setBulkBusy(verb === 'Dismissed' ? 'dismiss' : 'snooze');
    try {
      const outcome = await forEachCard(ids, fn);
      if (!outcome.done.length) throw new Error(outcome.firstError);
      setUndo({ msg: bulkMessage(verb, ids.length, outcome, suffix), itemIds: outcome.done });
      // The cards that failed stay ticked, so they can be tried again.
      setChecked(new Set(outcome.failed));
      anchorId.current = null;
      await load(true);
    } finally {
      setBulkBusy(null);
    }
  };
  const runBulk = (action: BulkAction) => {
    if (bulkBusy) return;
    if (action === 'claim') bulkClaim(false);
    else if (action === 'unclaim') bulkClaim(true);
    else setBulkDialog({ kind: action, ids: checkedItems.map(item => item.item_id) });
  };
  const dialogOpen = dialog !== null || bulkDialog !== null || paletteOpen || pickerOpen || stepDialogOpen;
  useKeymap({
    j: () => move(1), k: () => move(-1), ArrowDown: () => move(1), ArrowUp: () => move(-1),
    // A focused checkbox may sit on another row than the highlighted one, so Enter there opens nothing.
    // Enter on an action step's buttons presses the button.
    Enter: event => { if (event.target instanceof HTMLInputElement || (event.target as HTMLElement).closest?.('[data-action-steps]')) return false; if (selected) openDetail(selected); },
    // With cards ticked, C, S and X act on all of them; C claims what it can, else releases yours.
    c: () => { if (selecting) runBulk(claimable.length ? 'claim' : 'unclaim'); else claimSelected(); },
    // Mark done stays one card at a time: each one writes its own change log note.
    d: () => { if (selected && !selecting) setDialog('resolve'); },
    x: () => { if (selecting) runBulk('dismiss'); else if (selected) setDialog('dismiss'); },
    s: () => { if (selecting) runBulk('snooze'); else if (selected) setDialog('snooze'); },
    // Space ticks the focused row, or the highlighted one when focus is elsewhere.
    ' ': event => {
      const target = event.target instanceof HTMLElement && event.target !== document.body ? event.target : null;
      const id = target ? target.closest('[data-item-id]')?.getAttribute('data-item-id') : selectedId;
      // Any other focused control (a filter, a row's numbers toggle or table) keeps its own Space press.
      if (id && cards.some(item => item.item_id === id)) toggleCheck(id, false);
      else if (target) target.click();
      // Nothing highlighted or focused: Space scrolls the page as usual.
      else return false;
    },
    Escape: event => {
      const target = event.target as HTMLElement | null;
      const typing = target instanceof HTMLTextAreaElement || (target instanceof HTMLInputElement && target.type !== 'checkbox');
      if (selecting && !typing) clearChecked();
    },
    '/': () => searchRef.current?.focus(),
    'meta+k': () => setPaletteOpen(true), '?': () => setPaletteOpen(true),
  }, !dialogOpen);

  const paletteActions: PaletteAction[] = [
    ...(selecting ? [
      ...(claimable.length ? [{ id: 'bulk-claim', label: `Claim ${plural(claimable.length, 'selected card')}`, keys: 'C', run: () => runBulk('claim') }] : []),
      ...(releasable.length ? [{ id: 'bulk-unclaim', label: `Unclaim ${plural(releasable.length, 'selected card')}`, run: () => runBulk('unclaim') }] : []),
      { id: 'bulk-snooze', label: `Snooze ${plural(checkedItems.length, 'selected card')}`, keys: 'S', run: () => runBulk('snooze') },
      { id: 'bulk-dismiss', label: `Dismiss ${plural(checkedItems.length, 'selected card')}`, keys: 'X', run: () => runBulk('dismiss') },
      { id: 'bulk-clear', label: 'Clear selection', keys: 'Esc', run: clearChecked },
    ] : []),
    ...(cards.length && checkedItems.length < cards.length ? [{ id: 'select-all', label: `Select all ${cards.length} cards`, run: () => setChecked(new Set(cards.map(item => item.item_id))) }] : []),
    ...(selected && !selecting ? [
      { id: 'claim', label: selected.status === 'claimed' ? 'Unclaim' : 'Claim', keys: 'C', run: claimSelected },
      { id: 'done', label: 'Mark done', keys: 'D', run: () => setDialog('resolve') },
      { id: 'dismiss', label: 'Dismiss', keys: 'X', run: () => setDialog('dismiss') },
      { id: 'snooze', label: 'Snooze', keys: 'S', run: () => setDialog('snooze') },
      { id: 'open', label: 'Open item', keys: 'Enter', run: () => openDetail(selected) },
    ] : []),
    { id: 'next', label: 'Next item', keys: 'J', run: () => move(1) },
    { id: 'prev', label: 'Previous item', keys: 'K', run: () => move(-1) },
    { id: 'search', label: 'Search', keys: '/', run: () => searchRef.current?.focus() },
    ...BRANDS.filter(brand => brand.key !== query.brand).map(brand => ({ id: `brand-${brand.key}`, label: `Switch to ${brand.label}`, run: () => setQuery({ brand: brand.key, store: undefined }) })),
    { id: 'changelog', label: 'Go to Change Log', run: () => history.push('/change-log') },
    { id: 'refresh', label: 'Refresh', run: () => load() },
  ];

  if (!actorName) return (
    <RoundedPaper sx={{ maxWidth: 420, mx: 'auto', mt: 8, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="h6">Who are you?</Typography>
      <Typography variant="body2" color="text.secondary">Your name is stamped on claims, resolutions, and the change log.</Typography>
      {TEAM.map(name => <Button key={name} variant="outlined" onClick={() => setActorName(name)}>{name}</Button>)}
    </RoundedPaper>
  );

  const filtered = !!q.trim() || owner !== 'anyone' || myActive;
  const clearFilters = () => { setQ(''); setQuery({ owner: undefined }); setMyActive(false); };
  const filterReason = q.trim() ? `Nothing matches "${q.trim()}"`
    : owner === 'mine' ? 'Nothing is claimed by you'
    : owner === 'unclaimed' ? 'Nothing is unclaimed'
    : 'None of your campaigns has an open card';

  return (
    <Box sx={{ maxWidth: 1180, mx: 'auto', color: '#0F172A', pb: 7 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '6px 16px', flexWrap: 'wrap', pt: '22px', pb: '10px' }}>
        <Typography component="h1" sx={{ fontSize: '1.375rem', fontWeight: 700, lineHeight: 1.1, letterSpacing: '-0.01em' }}>Google Watchdog</Typography>
        <Typography sx={{ color: '#64748B', fontSize: '0.8125rem' }}>{!loading && `Checked ${formatTime(health?.finished_at ?? health?.last_success_at)} · `}next check {nextSlot(now)}</Typography>
      </Box>
      <BrandTabs query={query} items={items} loading={loading} onChange={setQuery} />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px 14px', pt: 1.75, pb: 2 }}>
        <StoreChips query={query} items={items} loading={loading} onChange={setQuery} />
        <Box component="label" sx={{ position: 'relative', flex: '1 1 220px', maxWidth: 340 }}>
          <SearchIcon aria-hidden="true" sx={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', fontSize: 16, color: '#64748B', pointerEvents: 'none' }} />
          <Box component="span" sx={srOnly}>Search cards</Box>
          <Box
            component="input"
            ref={searchRef}
            type="text"
            autoComplete="off"
            placeholder="Search campaigns and keywords"
            value={q}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setQ(event.target.value)}
            onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => { if (event.key === 'Escape') event.currentTarget.blur(); }}
            sx={{ width: '100%', font: 'inherit', fontSize: '0.875rem', lineHeight: 1.45, color: '#0F172A', bgcolor: '#FFFFFF', border: '1px solid #CBD5E1', borderRadius: '6px', pl: '30px', pr: '34px', py: '7px', '&::placeholder': { color: '#64748B', opacity: 1 }, '&:focus': { outline: 'none', borderColor: '#6366F1', boxShadow: '0 0 0 1px #6366F1' } }}
          />
          <Box sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', display: 'flex', pointerEvents: 'none' }}><Kbd>/</Kbd></Box>
        </Box>
        {expandable.length > 0 && (
          <Box sx={{ ...buttonGroup, ml: 'auto' }}>
            <Box component="button" type="button" aria-pressed={allOpen} onClick={() => setExpanded(allOpen ? new Set() : new Set(expandable))} sx={pressable(false)}>
              {allOpen ? 'Collapse all' : 'Expand all'}
            </Box>
          </Box>
        )}
        <Box role="group" aria-label="Owner" sx={{ ...buttonGroup, ml: expandable.length > 0 ? 0 : 'auto' }}>
          {(['anyone', 'mine', 'unclaimed'] as OwnerMode[]).map(mode => (
            <Box key={mode} component="button" type="button" aria-pressed={owner === mode} title={mode === 'mine' ? 'Cards you claimed' : undefined} onClick={() => setQuery({ owner: mode === 'anyone' ? undefined : mode })} sx={pressable(owner === mode)}>
              {mode === 'anyone' ? 'Anyone' : mode === 'mine' ? 'Claimed' : 'Unclaimed'}
            </Box>
          ))}
        </Box>
        <Box sx={buttonGroup}>
          <Box component="button" type="button" aria-pressed={myActive} onClick={() => myActive || myGroup?.campaign_ids.length ? setMyActive(value => !value) : openPicker('')} sx={pressable(myActive)}>My campaigns</Box>
          <Box component="button" type="button" aria-label="Choose my campaigns" title="Choose my campaigns" onClick={() => openPicker('')} sx={{ ...pressable(false), display: 'flex', alignItems: 'center', px: '8px' }}>
            <EditOutlinedIcon aria-hidden="true" sx={{ fontSize: 15 }} />
          </Box>
        </Box>
      </Box>
      {loadError && <Alert severity="error" sx={{ mb: 2 }} action={<Button onClick={() => load()}>Retry</Button>}>{loadError}</Alert>}
      <Box ref={listRef}>
        {loading && <Box component="section" aria-busy="true" sx={{ bgcolor: TODAY.wash, border: `1px solid ${TODAY.line}`, borderRadius: '8px', overflow: 'hidden', pt: 1.75, mb: 3.25 }}><SectionHead title="Urgent" note="Loading cards…" /><SkeletonRows showStore={showStore} withNumbers={campaignNumbers} /></Box>}
        {!loading && systemLines.length > 0 && <Box sx={{ display: 'flex', gap: '6px 12px', alignItems: 'baseline', flexWrap: 'wrap', px: 1.75, py: '9px', mb: '18px', border: '1px solid #CBD5E1', borderRadius: '6px', bgcolor: '#FFFFFF', fontSize: '0.84375rem' }}><Typography component="span" sx={{ color: '#475569', fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{systemLines.length} system alert{systemLines.length === 1 ? '' : 's'}</Typography><Box component="span" sx={{ flex: '1 1 320px', lineHeight: 1.6 }}>{systemLines.map((line, index) => <React.Fragment key={`${line}-${index}`}>{index > 0 && <br />}{line}</React.Fragment>)}</Box></Box>}
        {campaignNumbers && !loading && storesInView.filter(store => ACTION_STEP_STORES.includes(store)).map(store => (
          <ActionSteps key={store} store={store} itemId={null} title={`${showStore ? `${store} ` : ''}Action steps not on a card`}
            onCards={id => `/google-watchdog/triage/items/${encodeURIComponent(id)}`} onDialogChange={setStepDialogOpen} />
        ))}
        {campaignNumbers && !noneInView && <PerformanceTable stores={storesInView} showStore={showStore} mine={mineFilter} now={now} />}
        {!loading && !loadError && !cards.length && noneInView && <Box sx={{ px: 1.75, pt: 4.5, pb: 5, mb: 3.25, border: '1px solid #E2E8F0', borderRadius: '8px', bgcolor: '#FFFFFF' }}>
          <Typography component="h2" sx={{ fontSize: '1.125rem', fontWeight: 700 }}>You have no {scopeLabel} campaigns in My campaigns.</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
            <Box component="button" type="button" onClick={() => openPicker(query.store ? query.store.toLowerCase() : query.brand)} sx={{ ...plainButton, fontSize: '0.875rem', fontWeight: 700, color: '#FFFFFF', bgcolor: '#4F46E5', border: '1px solid #4F46E5', borderRadius: '6px', px: '14px', py: '8px', '&:hover': { bgcolor: '#4338CA', borderColor: '#4338CA' } }}>Add {scopeLabel} campaigns</Box>
            <Box component="button" type="button" onClick={() => setMyActive(false)} sx={{ ...plainButton, fontSize: '0.875rem', fontWeight: 700, color: '#0F172A', bgcolor: '#FFFFFF', border: '1px solid #CBD5E1', borderRadius: '6px', px: '14px', py: '8px', '&:hover': { bgcolor: '#F8FAFC' } }}>Show all</Box>
          </Box>
        </Box>}
        {!loading && !loadError && !cards.length && !noneInView && <Box sx={{ px: 1.75, pt: 4.5, pb: 5, mb: 3.25, border: '1px solid #E2E8F0', borderRadius: '8px', bgcolor: '#FFFFFF' }}>
          <Typography component="h2" sx={{ fontSize: '1.125rem', fontWeight: 700, mb: 0.75 }}>{filtered ? 'No cards match these filters.' : `Nothing open for ${scopeLabel}.`}</Typography>
          <Typography sx={{ color: '#475569' }}>{filtered ? `${filterReason} in ${scopeLabel}.` : `Last checked ${formatTime(health?.finished_at ?? health?.last_success_at)}. The next check runs at ${nextSlot(now)}.`}</Typography>
          {filtered && <Box component="button" type="button" onClick={clearFilters} sx={{ ...plainButton, mt: 1.5, fontSize: '0.875rem', fontWeight: 700, color: '#0F172A', bgcolor: '#FFFFFF', border: '1px solid #CBD5E1', borderRadius: '6px', px: '14px', py: '8px', '&:hover': { bgcolor: '#F8FAFC' } }}>Clear filters</Box>}
        </Box>}
        {!loading && (
          <>
            {!!cards.length && <BulkBar count={checkedItems.length} total={cards.length} claimable={claimable.length} releasable={releasable.length} busy={bulkBusy} onToggleAll={() => (checkedItems.length === cards.length ? clearChecked() : setChecked(new Set(cards.map(item => item.item_id))))} onAction={runBulk} />}
            {!!today.length && <Box component="section" aria-labelledby="watchdog-today" sx={{ bgcolor: TODAY.wash, border: `1px solid ${TODAY.line}`, borderRadius: '8px', overflow: 'hidden', pt: 1.75, mb: 3.25 }}><SectionHead id="watchdog-today" title="Urgent" /><ColumnHeader showStore={showStore} withNumbers={campaignNumbers} />{today.map((item, index) => <WatchdogRow key={item.item_id} item={item} index={index + 1} showStore={showStore} selected={item.item_id === selectedId} actorName={actorName} now={now} today withNumbers={campaignNumbers} open={expanded.has(item.item_id)} onToggle={toggleRow} onOpen={openDetail} checked={checked.has(item.item_id)} onCheck={toggleCheck} />)}</Box>}
            {!!rest.length && <Box component="section" aria-labelledby="watchdog-rest" sx={{ mb: 3.25 }}><SectionHead id="watchdog-rest" title="Remaining Issues" />{rest.map((item, index) => <WatchdogRow key={item.item_id} item={item} index={index + TODAY_CAP + 1} showStore={showStore} selected={item.item_id === selectedId} actorName={actorName} now={now} withNumbers={campaignNumbers} open={expanded.has(item.item_id)} onToggle={toggleRow} onOpen={openDetail} checked={checked.has(item.item_id)} onCheck={toggleCheck} />)}</Box>}
            {!!recoveredView.length && <Box component="section" aria-labelledby="watchdog-closed" sx={{ mb: 3.25 }}>
              <Typography component="h2" sx={{ px: 1.75, mb: 1, fontSize: '1.0625rem', fontWeight: 700, lineHeight: 1.2 }}>
                <Box component="button" type="button" id="watchdog-closed" aria-expanded={closedOpen} aria-controls="watchdog-closed-rows" onClick={() => setClosedOpen(open => !open)} sx={{ ...plainButton, display: 'inline-flex', alignItems: 'center', gap: '8px', p: 0, border: 0, bgcolor: 'transparent', color: 'inherit', fontWeight: 'inherit', lineHeight: 'inherit', '& svg': { color: '#64748B' }, '&:hover svg': { color: '#0F172A' } }}>
                  Closed in the last 3 days
                  <Box component="span" sx={{ color: '#64748B', fontSize: '0.8125rem', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{recoveredView.length}</Box>
                  <ExpandMoreIcon aria-hidden="true" sx={{ fontSize: 20, transform: closedOpen ? 'rotate(180deg)' : 'none', transition: 'transform 160ms cubic-bezier(0.25, 1, 0.5, 1)', '@media (prefers-reduced-motion: reduce)': { transition: 'none' } }} />
                </Box>
              </Typography>
              <Box id="watchdog-closed-rows" hidden={!closedOpen}>{recoveredView.map(item => <WatchdogRow key={item.item_id} item={item} index={null} showStore={showStore} selected={false} actorName={actorName} now={now} closed withNumbers={campaignNumbers} onOpen={openDetail} />)}</Box>
            </Box>}
          </>
        )}
      </Box>
      <Box component="p" sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 16px', m: 0, color: '#64748B', fontSize: '0.78125rem' }}>
        <span><Kbd>J</Kbd> <Kbd>K</Kbd> move</span><span><Kbd>Enter</Kbd> open</span><span><Kbd>Space</Kbd> select</span><span><Kbd>/</Kbd> search</span>
      </Box>
      {selected && <React.Fragment key={selected.item_id}><ResolveDialog open={dialog === 'resolve'} onClose={() => setDialog(null)} headline={problemHeadline(selected)} onResolve={async (note, kind) => { await service.resolve(selected.item_id, note, kind); setUndo({ msg: 'Done, logged to the change log', itemIds: [selected.item_id] }); await load(); }} /><DismissDialog open={dialog === 'dismiss'} onClose={() => setDialog(null)} headline={problemHeadline(selected)} onDismiss={async reason => { await service.dismiss(selected.item_id, reason); setUndo({ msg: 'Dismissed', itemIds: [selected.item_id] }); await load(); }} /><SnoozeDialog open={dialog === 'snooze'} onClose={() => setDialog(null)} headline={problemHeadline(selected)} onSnooze={async until => { await service.snooze(selected.item_id, until); setUndo({ msg: `Snoozed until ${until}`, itemIds: [selected.item_id] }); await load(); }} /></React.Fragment>}
      {bulkDialog?.kind === 'dismiss' && <DismissDialog open onClose={() => setBulkDialog(null)} headline={plural(bulkDialog.ids.length, 'card')} onDismiss={reason => bulkHold('Dismissed', bulkDialog.ids, id => service.dismiss(id, reason))} />}
      {bulkDialog?.kind === 'snooze' && <SnoozeDialog open onClose={() => setBulkDialog(null)} headline={plural(bulkDialog.ids.length, 'card')} onSnooze={until => bulkHold('Snoozed', bulkDialog.ids, id => service.snooze(id, until), ` until ${until}`)} />}
      <CampaignPicker open={pickerOpen} onClose={() => setPickerOpen(false)} loadCampaigns={loadPickerCampaigns} initialFilter={pickerFilter} initialSelected={myGroup?.campaign_ids ?? []} onSave={async ids => { await service.saveGroup({ groupId: myGroup?.group_id, groupName: 'default', campaignIds: ids }); const groups = await service.listGroups(); setMyGroup(groups.find(group => group.owner === actorName) ?? null); setMyActive(ids.length > 0); }} />
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} actions={paletteActions} />
      <Snackbar open={!!toast} autoHideDuration={3500} onClose={() => setToast('')} message={toast} />
      <Snackbar key={`undo-${undo?.itemIds.join(',') ?? 'none'}`} open={!!undo} autoHideDuration={8000} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} onClose={() => setUndo(null)} message={undo?.msg} action={<Button color="secondary" size="small" onClick={async () => { const target = undo; setUndo(null); if (!target) return; const outcome = await forEachCard(target.itemIds, id => service.reopen(id)); setToast(target.itemIds.length === 1 ? (outcome.failed.length ? outcome.firstError : 'Reopened') : bulkMessage('Reopened', target.itemIds.length, outcome)); await load(); }}>UNDO</Button>} />
    </Box>
  );
}
